import type {
  TimeOffRequestDto,
  TimeOffRequestStatus,
  RequestDayStatus,
  ApprovalStepStatus,
} from '../types';

const REQUEST_STATUS_BY_LOWERCASE: Record<string, TimeOffRequestStatus> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

const DAY_STATUS_BY_LOWERCASE: Record<string, RequestDayStatus> = {
  active: 'Active',
  cancelled: 'Cancelled',
};

const STEP_STATUS_BY_LOWERCASE: Record<string, ApprovalStepStatus> = {
  waiting: 'Waiting',
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  skipped: 'Skipped',
};

/**
 * Normalizes the enum-shaped string fields of a `TimeOffRequestDto` —
 * the backend serializes `status` (on the request, each day, and each
 * approval step) in lowercase, but every frontend consumer (`types.ts`'
 * unions, the `STATUS_COLOR`/`DAY_CELL_COLOR` maps, `requests.status.*`
 * i18n keys) is PascalCase. Values that don't match a known lowercase key
 * pass through unchanged rather than being dropped, so an unexpected value
 * degrades to "no styling" instead of silently losing data.
 *
 * Apply at every read boundary in `time-off.service.ts` so no consumer has
 * to special-case casing itself.
 */
export function normalizeTimeOffRequest(request: TimeOffRequestDto): TimeOffRequestDto {
  return {
    ...request,
    status: request.status
      ? (REQUEST_STATUS_BY_LOWERCASE[request.status.toLowerCase()] ?? request.status)
      : request.status,
    days:
      request.days?.map((day) => ({
        ...day,
        status: day.status
          ? (DAY_STATUS_BY_LOWERCASE[day.status.toLowerCase()] ?? day.status)
          : day.status,
      })) ?? request.days,
    steps:
      request.steps?.map((step) => ({
        ...step,
        status: step.status
          ? (STEP_STATUS_BY_LOWERCASE[step.status.toLowerCase()] ?? step.status)
          : step.status,
      })) ?? request.steps,
  };
}
